import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2, RotateCcw, Save, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { RecipientsEditor } from "./RecipientsEditor";
import { BlocksEditor, PlaceholderList } from "./BlocksEditor";
import { apiError } from "../../pages/SowEmails";
import { useAuth } from "../../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const draftOf = (t) => ({
  subject: t.subject, title: t.title, blocks: { ...t.blocks }, high_importance: !!t.high_importance,
  recipients: { to_roles: [...t.recipients.to_roles], to_extra: [...t.recipients.to_extra] },
});

export function EmailEditor({ entry, roles, alertList, onSaved, onPreview }) {
  const { user } = useAuth();
  const [testTo, setTestTo] = useState(user?.email || "");
  const [draft, setDraft] = useState(() => draftOf(entry.template));
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [more, setMore] = useState(false);
  const timer = useRef(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(entry.template));

  useEffect(() => {
    if (!dirty) { setError(null); onPreview(null); return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/api/sow-emails/${entry.kind}/preview`, draft);
        onPreview(data);
        setError(null);
      } catch (e) {
        setError(apiError(e, "The preview could not be rendered"));
      }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [draft, dirty, entry.kind, onPreview]);

  const run = async (label, fn, ok) => {
    setBusy(label);
    setError(null);
    try {
      const { data } = await fn();
      if (data?.template) { onSaved(data); setDraft(draftOf(data.template)); onPreview(null); }
      toast.success(ok(data));
    } catch (e) {
      setError(apiError(e, "Something went wrong"));
    } finally {
      setBusy(null);
    }
  };
  const save = () => run("save", () => axios.put(`${API}/api/sow-emails/${entry.kind}`, draft), () => "Email saved");
  const reset = () => run("reset", () => axios.post(`${API}/api/sow-emails/${entry.kind}/reset`), () => "Back to the built in wording and recipients");
  const test = () => run("test", () => axios.post(`${API}/api/sow-emails/${entry.kind}/test`, { to: testTo }), (d) => `Test sent to ${d.sent_to}`);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col gap-5 min-w-0" data-testid={`sow-email-editor-${entry.kind}`}>
      <div>
        <h2 className="text-lg font-semibold text-stone-900" data-testid="sow-email-title">{entry.label}</h2>
        <p className="text-sm text-stone-600 mt-1" data-testid="sow-email-trigger">{entry.trigger}</p>
        {entry.attachments && <p className="text-xs text-stone-500 mt-1">Attachment: {entry.attachments}</p>}
        {entry.template.updated_at && (
          <p className="text-xs text-amber-700 mt-1" data-testid="sow-email-edited-note">
            Edited by {entry.template.updated_by} on {new Date(entry.template.updated_at).toLocaleString("en-GB")}.
          </p>
        )}
      </div>
      <RecipientsEditor kind={entry} roles={roles} alertList={alertList} value={draft.recipients} onChange={(recipients) => setDraft((d) => ({ ...d, recipients }))} />
      <label className="flex flex-col gap-1.5 text-sm">
        <span className="font-medium text-stone-800">Subject line</span>
        <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} data-testid="sow-email-subject" />
      </label>
      <BlocksEditor blocks={entry.blocks} value={draft.blocks} onChange={(key, text) => setDraft((d) => ({ ...d, blocks: { ...d.blocks, [key]: text } }))} />
      <Collapsible open={more} onOpenChange={setMore}>
        <CollapsibleTrigger className="inline-flex items-center gap-1 text-sm text-[#1B4F8A] hover:underline" data-testid="sow-email-more">
          <ChevronDown className={`w-4 h-4 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Fewer options" : "More options"}
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-4 pt-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-stone-800">Heading inside the email</span>
            <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="sow-email-heading" />
          </label>
          <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
            <Checkbox checked={draft.high_importance} onCheckedChange={(v) => setDraft((d) => ({ ...d, high_importance: !!v }))} className="mt-0.5" data-testid="sow-email-high-importance" />
            <span>Mark as high importance <span className="text-stone-500">· the red exclamation mark in Outlook</span></span>
          </label>
          <PlaceholderList placeholders={entry.placeholders} />
        </CollapsibleContent>
      </Collapsible>
      {error && <p className="text-sm text-rose-700" data-testid="sow-email-error">{error}</p>}
      <div className="flex items-center gap-3 flex-wrap pt-1 border-t border-stone-100">
        <Button onClick={save} disabled={!dirty || !!busy} className="bg-[#1B4F8A] hover:bg-[#173f6e] mt-4" data-testid="sow-email-save">
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
        </Button>
        <Button variant="outline" onClick={reset} disabled={!!busy || !entry.template.customised} className="mt-4" data-testid="sow-email-reset">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
        </Button>
        <span className="inline-flex items-center gap-2 mt-4">
          <Button variant="outline" onClick={test} disabled={!!busy || dirty || !testTo} title={dirty ? "Save first" : "Sends the saved version to the address on the right"} data-testid="sow-email-test">
            {busy === "test" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send a test to
          </Button>
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="name@medicysltd.co.uk" className="w-60 h-9" data-testid="sow-email-test-to" />
        </span>
        {dirty && <span className="text-xs text-amber-700 mt-4" data-testid="sow-email-unsaved">Unsaved changes</span>}
      </div>
    </div>
  );
}
